import { RadioOption } from '@components/radio/Radio'
import { COLORS } from '@components/incidentStatus/IncidentStatus'

import * as IncidentStore from '@store/incident/incident'
import * as UserStore from '@store/user/user'
import * as FamilleStore from '@store/famille/famille'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'

import api from '@api/api'

import { isBefore, differenceInHours } from 'date-fns'
import sanitize from '@utils/Sanitizer'
import { hasCoordinate } from '@utils/incidentUtils'
import { OPEN_INCIDENT_STATES } from '@utils/useSiteChangeHandler'

import { TypeUtilisateur } from '@api/enum'

const flatMap = (array: any[][]) => {
  return array.reduce((acc, cur) => [...acc, ...cur], [])
}

const findLocalisationSubLevels = (
  places: LocalisationResponse[],
  space: LocalisationResponse | undefined
): LocalisationResponse[] => {
  if (!space) {
    return []
  }

  const children = places.filter((p) => p.parentId === space.id)

  if (children.length === 0) {
    return []
  }

  return [...children, ...flatMap(children.map((c) => findLocalisationSubLevels(places, c)))]
}

const findLocalisationParents = (
  places: LocalisationResponse[],
  space: LocalisationResponse
): LocalisationResponse[] => {
  if (!space) {
    return []
  }

  const parent = places.find((p) => p.id === space.parentId)

  if (!parent) {
    return [space]
  }

  if (!parent.parentId || space.id === space.parentId) {
    return [parent, space]
  }

  return [...findLocalisationParents(places, parent), space]
}

const findFamilyParents = (activities: FamilleResponse[], prestation: FamilleResponse): FamilleResponse[] => {
  if (!prestation) {
    return []
  }

  const parent = activities.find((p) => p.id === prestation.parentIdFamille)

  if (!parent) {
    return [prestation]
  }

  if (!parent.parentIdFamille || prestation.id === prestation.parentIdFamille) {
    return [parent, prestation]
  }

  return [...findFamilyParents(activities, parent), prestation]
}

const orderResult = (data: IncidentResponse[], options: { sort: RadioOption; reverse: boolean }) => {
  return data.sort((a, b) => {
    const hasDatePriseEnCharge = !!a.datePriseEnCharge && !!b.datePriseEnCharge
    const sortResult =
      !options.sort || options.sort.value === 'createdAt'
        ? isBefore(new Date(a.dateDeCreation), new Date(b.dateDeCreation))
          ? 1
          : -1
        : options.sort.value === 'modifiedAt'
        ? hasDatePriseEnCharge && isBefore(new Date(a.datePriseEnCharge!), new Date(b.datePriseEnCharge!))
          ? 1
          : -1
        : options.sort.value === 'number'
        ? a.numero < b.numero
          ? 1
          : -1
        : 1

    return sortResult * (options.reverse ? -1 : 1)
  })
}

const applyFilterToList = (
  incidents: IncidentResponse[],
  filters: Filters,
  user: UserResponse,
  sources: SearchSource,
  i18n: I18nState,
  activities: FamilleResponse[],
  searchText?: string,
  options?: {
    sort: RadioOption
    reverse: boolean
  }
) => {
  if (!incidents) {
    return []
  }

  const sanitizedSearch = sanitize(searchText)

  const filteredData = incidents.filter((incident) => {
    const validation = []
    const prestation = sources.activities.find((a) => a.id === incident.famillePrestationId)
    const manifestation = sources.manifestations.find((m) => m.id === incident.manifestationId)
    const familly = prestation && findFamilyParents(sources.activities, prestation)
    const place = sources.places.find((p) => p.id === incident.localisationId)
    const locationPath = place ? findLocalisationParents(sources.places, place) : []
    const type = sanitize(
      i18n.t(`screens.incidentList.card.type.${incident.isInfrastructure ? `infrastructure` : `prestation`}`)
    )
    const provider = sources.providers.find((p) => p.id === incident.societePrestataireId)

    if (filters.manifestation.length > 0) {
      const value = filters.manifestation.some((e) => e.id === incident.manifestationId)
      validation.push(value)
    }

    if (filters.category.length > 0) {
      const value = filters.category.some((e) => (e.id === 'infrastructure') === incident.isInfrastructure)
      validation.push(value)
    }

    if (filters.activity.length > 0) {
      const prestation = activities.find((act) => act.id === incident.famillePrestationId)
      const famillyIds = prestation && findFamilyParents(activities, prestation).map((f) => f.id)

      const value = filters.activity.some((e) => famillyIds && famillyIds.includes(e.id))
      validation.push(value)
    }

    if (filters.status.length > 0) {
      const value = filters.status.some((e) => e === incident.status)
      validation.push(value)
    }

    if (filters.location.length > 0) {
      const value = filters.location.some((e) => !!locationPath.find((loc) => loc.id === e.id))
      validation.push(value)
    }

    if (filters.followed) {
      validation.push(sources.favoriteIncidents.map((f) => f.id).includes(incident.id))
    }

    if (filters.created) {
      validation.push(incident.utilisateurDeclarantId === user.id)
    }

    if (filters.isNew) {
      // "Nouvelle" demande = les 24 dernières heures
      validation.push(differenceInHours(new Date(), new Date(incident.dateDeCreation)) < 24)
    }

    if (filters.isMap) {
      validation.push(hasCoordinate(incident))
    }

    if (!!searchText && searchText.length > 0) {
      const value =
        sanitize(incident.numero.toString()).includes(sanitizedSearch) ||
        (prestation && sanitize(prestation.libelle).includes(sanitizedSearch)) ||
        (manifestation && sanitize(manifestation.libelle).includes(sanitizedSearch)) ||
        (familly && sanitize(familly[0].libelle).includes(sanitizedSearch)) ||
        (provider && sanitize(provider.nameSociete).includes(sanitizedSearch)) ||
        locationPath.map((l) => sanitize(l.libelle).includes(sanitizedSearch)).some((b) => b) ||
        (incident.commandeAssociee &&
          incident.commandeAssociee.enseigne &&
          sanitize(incident.commandeAssociee.enseigne).includes(sanitizedSearch)) ||
        type.includes(sanitizedSearch)
      validation.push(value)
    }

    return !!validation.every((e) => e)
  })

  if (!!options) {
    return orderResult(filteredData, options)
  }
  return filteredData
}

const getIncidentData = (site: SiteResponse | undefined, callback: () => void) => {
  if (!!site) {
    Promise.all([
      api.incident.search({ siteId: site.id, etatsIncident: OPEN_INCIDENT_STATES }),
      api.incident.followed(),
      api.familles.all(site.id, true),
      api.localisation.all(site.id, true),
      api.societe.all(site.id),
    ])
      .then(([incidents, followed, activities, places, providers]) => {
        IncidentStore.actions.setIncidents(incidents)
        UserStore.actions.setFavoriteIncidents(followed)
        FamilleStore.actions.setActivities(activities)
        LocationStore.actions.setPlaces(places)
        SocieteStore.actions.setServices(providers)
      })
      .finally(callback)
  }
}

const getMapPins = (data: IncidentResponse[]) => {
  return data
    .filter((incident) => !!incident.plan && !!incident.plan.x && !!incident.plan.y)
    .map((incident) => ({
      id: incident.id,
      x: parseFloat(incident.plan!.x),
      y: parseFloat(incident.plan!.y),
      color: COLORS[incident.status].default,
    }))
}

const sortEnseigneIncidents = (incidents: IncidentResponse[], reverse: boolean) => {
  const dataWithEnseigne = incidents.filter(
    (incident) => !!incident.commandeAssociee && !!incident.commandeAssociee.enseigne
  )
  const dataWithoutEnseigne = incidents.filter(
    (incident) => !incident.commandeAssociee || !incident.commandeAssociee.enseigne
  )

  const sorted = dataWithEnseigne.sort((a, b) => {
    return a.commandeAssociee!.enseigne!.localeCompare(b.commandeAssociee!.enseigne!) * (reverse ? -1 : 1)
  })

  return [...sorted, ...dataWithoutEnseigne]
}

const displayLocationString = (
  incident: IncidentResponse,
  locations: LocalisationResponse[],
  manifestation?: ManifestationResponse
) => {
  const infraLocs = locations ? locations.map((l) => l && l.libelle).filter(Boolean) : []
  if (incident.isInfrastructure) {
    // Infrastructure sur manifestation
    if (manifestation) {
      return `${manifestation.libelle}${infraLocs.length > 0 ? ` - ${infraLocs.join(' - ')}` : ''}`
    }
    // Infrastructure hors manifestation
    return infraLocs.join(' - ')
  }
  // Prestation sur manifestation
  if (manifestation) {
    const prestaLocs = incident.commandeAssociee
      ? [
          incident.commandeAssociee.espace,
          incident.commandeAssociee.allee,
          incident.commandeAssociee.stand,
          incident.commandeAssociee.enseigne,
        ].filter(Boolean)
      : []
    return `${manifestation.libelle}${prestaLocs.length > 0 ? ` - ${prestaLocs.join(' - ')}` : ''}`
  }

  // Cas non autorisé : prestation hors manifestation + non infra... On retourne la localisation infra quand même
  return infraLocs.length > 0 ? `${infraLocs.join(' - ')}` : ''
}

const formHasUser = (form: IncidentForm) =>
  !!form.nomContact && !!form.prenomContact && !!form.emailContact && !!form.telephoneContact

const canUserCreateIncident = (user?: UserResponse, manifestation?: ManifestationResponse) => {
  if (!user) {
    // can create as not connected
    return true
  }

  if (user.typeUtilisateur === TypeUtilisateur.ORGANISATEUR && !manifestation) {
    // can create only if manifestation (must be in progress)
    return false
  }

  return true
}

export {
  getMapPins,
  findLocalisationSubLevels,
  findLocalisationParents,
  findFamilyParents,
  applyFilterToList,
  getIncidentData,
  sortEnseigneIncidents,
  displayLocationString,
  formHasUser,
  canUserCreateIncident,
}
